import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpDown, ArrowDown, ArrowUp, X } from "lucide-react";
import { scListSifs } from "../../lib/sifcontrolApi";

const RETURN_COLS = [
    { key: "return_1d", label: "1D" },
    { key: "return_1m", label: "1M" },
    { key: "return_3m", label: "3M" },
    { key: "return_6m", label: "6M" },
    { key: "return_1y", label: "1Y" },
    { key: "cagr_2y", label: "2Y" },
    { key: "cagr_3y", label: "3Y" },
];

const PLAN_OPTIONS = ["", "Direct", "Regular"];
const OPTION_OPTIONS = ["", "Growth", "IDCW"];

const fmtPct = (m) => {
    if (!m || m.value === null || m.value === undefined || !m.eligible) {
        return { text: "—", cls: "text-oth-ink3" };
    }
    const v = m.value;
    const sign = v > 0 ? "+" : "";
    return {
        text: `${sign}${v.toFixed(2)}%`,
        cls: v > 0 ? "text-green-700" : v < 0 ? "text-red-700" : "text-oth-ink2",
    };
};

const returnValue = (row, key) => {
    const m = row.returns?.[key];
    return m?.eligible && m.value !== null && m.value !== undefined ? m.value : null;
};

export default function SifControlSifList() {
    const [params, setParams] = useSearchParams();
    const [q, setQ] = useState(params.get("q") || "");
    const [plan, setPlan] = useState(params.get("plan") || "");
    const [option, setOption] = useState(params.get("option") || "");
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [sortKey, setSortKey] = useState("sif_name");
    const [sortDir, setSortDir] = useState("asc");

    useEffect(() => {
        setLoading(true);
        scListSifs({ q, plan, option, limit: 200 })
            .then((r) => { setItems(r.items || []); setTotal(r.total || 0); })
            .catch(() => { setItems([]); setTotal(0); })
            .finally(() => setLoading(false));
        // Persist query params so links/reload stay stable.
        const next = new URLSearchParams();
        if (q) next.set("q", q);
        if (plan) next.set("plan", plan);
        if (option) next.set("option", option);
        setParams(next, { replace: true });
    }, [q, plan, option, setParams]);

    const sorted = useMemo(() => {
        const arr = [...items];
        const maxAum = arr.reduce((m, s) => Math.max(m, s.aum_latest?.aum || 0), 0) || 1;
        arr.forEach((s) => { s._aumPct = ((s.aum_latest?.aum || 0) / maxAum) * 100; });
        arr.sort((a, b) => {
            let va, vb;
            if (sortKey === "sif_name") { va = a.sif_name || ""; vb = b.sif_name || ""; }
            else if (sortKey === "strategy_category") { va = a.strategy_category || ""; vb = b.strategy_category || ""; }
            else if (sortKey === "aum") { va = a.aum_latest?.aum ?? null; vb = b.aum_latest?.aum ?? null; }
            else { va = returnValue(a, sortKey); vb = returnValue(b, sortKey); }
            const aNull = va === null || va === undefined || va === "";
            const bNull = vb === null || vb === undefined || vb === "";
            if (aNull && bNull) return 0;
            if (aNull) return 1;
            if (bNull) return -1;
            if (typeof va === "number" && typeof vb === "number") {
                return sortDir === "asc" ? va - vb : vb - va;
            }
            return sortDir === "asc"
                ? String(va).localeCompare(String(vb))
                : String(vb).localeCompare(String(va));
        });
        return arr;
    }, [items, sortKey, sortDir]);

    const toggleSort = (key) => {
        if (key === sortKey) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        else {
            setSortKey(key);
            setSortDir(key === "sif_name" || key === "strategy_category" ? "asc" : "desc");
        }
    };

    const clearFilters = () => { setQ(""); setPlan(""); setOption(""); };
    const activeFilterCount = (q ? 1 : 0) + (plan ? 1 : 0) + (option ? 1 : 0);

    const SortIcon = ({ col }) => {
        if (sortKey !== col) return <ArrowUpDown size={10} className="opacity-40" />;
        return sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />;
    };

    return (
        <section className="py-14 md:py-20" data-testid="sc-sifs-page">
            <div className="mx-auto max-w-[1400px] px-4 md:px-6">
                <div className="text-[11px] uppercase tracking-[0.24em] text-oth-ink3 font-body">SIFs</div>
                <h1 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight text-oth-ink">All SIFs</h1>

                {/* Filter bar */}
                <div className="mt-8 flex flex-wrap items-end gap-3" data-testid="sc-sifs-filters">
                    <label className="block flex-1 min-w-[260px] max-w-[420px]">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-oth-ink3 font-body mb-1.5">Search</div>
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            className="w-full h-11 px-4 rounded-full border border-oth-line bg-white text-sm focus:outline-none focus:border-oth-ink transition"
                            placeholder="Name, code or strategy"
                            data-testid="sc-sifs-search"
                        />
                    </label>
                    <label className="block">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-oth-ink3 font-body mb-1.5">Plan</div>
                        <select
                            value={plan}
                            onChange={(e) => setPlan(e.target.value)}
                            className="h-11 px-4 pr-10 rounded-full border border-oth-line bg-white text-sm focus:outline-none focus:border-oth-ink transition min-w-[140px]"
                            data-testid="sc-sifs-plan"
                        >
                            {PLAN_OPTIONS.map((p) => <option key={p} value={p}>{p || "All plans"}</option>)}
                        </select>
                    </label>
                    <label className="block">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-oth-ink3 font-body mb-1.5">Option</div>
                        <select
                            value={option}
                            onChange={(e) => setOption(e.target.value)}
                            className="h-11 px-4 pr-10 rounded-full border border-oth-line bg-white text-sm focus:outline-none focus:border-oth-ink transition min-w-[140px]"
                            data-testid="sc-sifs-option"
                        >
                            {OPTION_OPTIONS.map((o) => <option key={o} value={o}>{o || "All options"}</option>)}
                        </select>
                    </label>
                    {activeFilterCount > 0 && (
                        <button
                            onClick={clearFilters}
                            className="btn-pill !text-xs !py-2 !px-3 h-11"
                            data-testid="sc-sifs-clear-filters"
                        >
                            <X size={12} /> Clear filters ({activeFilterCount})
                        </button>
                    )}
                </div>

                <div className="mt-8 rounded-3xl border border-oth-line bg-white overflow-hidden">
                    {loading ? (
                        <div className="p-10 text-center text-sm font-body text-oth-ink3">Loading…</div>
                    ) : sorted.length === 0 ? (
                        <div className="p-10 text-center text-sm font-body text-oth-ink3" data-testid="sc-sifs-empty">
                            No SIFs match. Try clearing filters — the universe grows as AMFI publishes daily NAVs.
                        </div>
                    ) : (
                        <div className="overflow-x-auto thin-scroll" data-testid="sc-sifs-list">
                            <table className="w-full min-w-[1180px] text-[13px] font-body">
                                <thead>
                                    <tr className="text-[10px] uppercase tracking-[0.16em] text-oth-ink3 font-body bg-oth-bg-2/60 border-b border-oth-line">
                                        <th
                                            className="text-left px-5 py-3 sticky left-0 z-10 bg-white/95 backdrop-blur cursor-pointer select-none sc-sortable"
                                            onClick={() => toggleSort("sif_name")}
                                            data-testid="sc-sifs-sort-name"
                                        >
                                            <span className="inline-flex items-center gap-1">SIF <SortIcon col="sif_name" /></span>
                                        </th>
                                        <th
                                            className="text-left px-4 py-3 cursor-pointer select-none hidden lg:table-cell sc-sortable"
                                            onClick={() => toggleSort("strategy_category")}
                                            data-testid="sc-sifs-sort-strategy"
                                        >
                                            <span className="inline-flex items-center gap-1">Strategy <SortIcon col="strategy_category" /></span>
                                        </th>
                                        <th className="text-left px-3 py-3 whitespace-nowrap hidden md:table-cell">Plan · Option</th>
                                        {RETURN_COLS.map((c) => (
                                            <th
                                                key={c.key}
                                                className="text-right px-3 py-3 cursor-pointer select-none whitespace-nowrap sc-sortable"
                                                onClick={() => toggleSort(c.key)}
                                                data-testid={`sc-sifs-sort-${c.key}`}
                                            >
                                                <span className="inline-flex items-center gap-1 justify-end w-full">{c.label} <SortIcon col={c.key} /></span>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {sorted.map((s) => (
                                        <tr key={s.id} className="sc-row border-b border-oth-line/70 last:border-b-0 hover:bg-oth-bg-2 transition-colors">
                                            <td className="px-5 py-3 sticky left-0 z-10 bg-white/95 backdrop-blur">
                                                <Link
                                                    to={`/sifcontrol/sif/${s.id}`}
                                                    className="block"
                                                    data-testid={`sc-sifs-row-${s.id}`}
                                                >
                                                    <div className="font-display text-sm font-medium text-oth-ink truncate max-w-[320px]">{s.sif_name}</div>
                                                    <div className="text-[11px] font-body text-oth-ink3 mt-0.5">{s.sif_code}</div>
                                                </Link>
                                            </td>
                                            <td className="px-4 py-3 text-xs font-body text-oth-ink2 hidden lg:table-cell">{s.strategy_category || "—"}</td>
                                            <td className="px-3 py-3 hidden md:table-cell">
                                                <div className="flex flex-wrap gap-1">
                                                    {s.plan && <span className="sc-chip sc-chip-muted">{s.plan}</span>}
                                                    {s.option && <span className="sc-chip">{s.option}</span>}
                                                </div>
                                            </td>
                                            {RETURN_COLS.map((c) => {
                                                const f = fmtPct(s.returns?.[c.key]);
                                                return (
                                                    <td
                                                        key={c.key}
                                                        className={`px-3 py-3 text-right font-body tabular-nums ${f.cls}`}
                                                        data-testid={`sc-sifs-${c.key}-${s.id}`}
                                                    >
                                                        {f.text}
                                                    </td>
                                                );
                                            })}
                                            <td
                                                className="px-3 py-3 text-right font-body tabular-nums text-oth-ink relative"
                                                data-testid={`sc-sifs-aum-${s.id}`}
                                            >
                                                {s.aum_latest?.aum != null && (
                                                    <div
                                                        aria-hidden
                                                        className="absolute right-1 top-1/2 -translate-y-1/2 h-6 rounded-md pointer-events-none"
                                                        style={{
                                                            width: `${Math.max(6, s._aumPct)}%`,
                                                            background: "linear-gradient(90deg, transparent, rgba(75,127,255,0.18))",
                                                            zIndex: 0,
                                                        }}
                                                    />
                                                )}
                                                <span className="relative z-10">
                                                    {s.aum_latest?.aum != null
                                                        ? `₹${Number(s.aum_latest.aum).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`
                                                        : "—"}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                <div className="mt-4 text-[11px] font-body text-oth-ink3">
                    {total} SIFs · showing first {items.length}. Returns computed from AMFI NAV history; dashes indicate insufficient history.
                </div>
            </div>
        </section>
    );
}
